import { Phone, MessageCircle, Mail, UserPlus, Globe, MapPin, QrCode, Instagram, Facebook, Linkedin, Music2, Building2 } from "lucide-react";

function parseHex(hex) {
  const h = (hex || "").replace("#", "");
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
}

function contrastText(hex) {
  try {
    const [r, g, b] = parseHex(hex);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.62 ? "#0a0a0a" : "#ffffff";
  } catch {
    return "#ffffff";
  }
}

// amt in [-1,1]: negativo scurisce, positivo schiarisce
function shade(hex, amt) {
  try {
    const [r, g, b] = parseHex(hex);
    const clamp = (c) => Math.max(0, Math.min(255, Math.round(c + amt * 255)));
    return `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`;
  } catch {
    return hex;
  }
}

function hexA(hex, a) {
  try {
    const [r, g, b] = parseHex(hex);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  } catch {
    return hex;
  }
}

// Logica theme condivisa da anteprima live E pagina pubblica.
export function getTheme(data) {
  const template = data.template;

  if (template === "bianco") {
    const accent = data.accent_color || "#0F172A";
    const txt = contrastText(accent);
    return {
      pageStyle: {},
      pageClass: "bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900",
      cardClass: "bg-white/90 border border-slate-200 shadow-xl backdrop-blur-md",
      saveBtn: "font-bold hover:brightness-105",
      saveBtnStyle: { backgroundColor: accent, color: txt, boxShadow: `0 10px 30px -10px ${hexA(accent, 0.5)}` },
      secondaryBtn: "hover:brightness-95 font-semibold",
      secondaryBtnStyle: { backgroundColor: hexA(accent, 0.1), color: "#0f172a", border: `1px solid ${hexA(accent, 0.35)}` },
      muted: "text-slate-500",
      ring: "",
      ringStyle: { boxShadow: `0 0 0 4px ${hexA(accent, 0.3)}` },
      qrColor: accent,
    };
  }

  if (template === "custom") {
    const primary = data.custom_color || "#4F46E5";   // colore dominante di sfondo
    const secondary = data.secondary_color || "#0EA5E9"; // accento: pulsante, icone, bordi
    const pageTxt = contrastText(primary);
    return {
      // sfondo dominato dal colore principale (gradiente verso una sua variante più scura)
      pageStyle: { background: `linear-gradient(160deg, ${shade(primary, 0.08)} 0%, ${primary} 45%, ${shade(primary, -0.28)} 100%)`, color: pageTxt },
      pageClass: "",
      cardClass: "bg-white/95 text-slate-900 border border-white/40 shadow-2xl backdrop-blur-2xl",
      saveBtn: "font-bold hover:brightness-110",
      saveBtnStyle: { backgroundColor: secondary, color: contrastText(secondary), boxShadow: `0 10px 30px -8px ${hexA(secondary, 0.6)}` },
      secondaryBtn: "hover:brightness-110 font-semibold",
      secondaryBtnStyle: { backgroundColor: hexA(secondary, 0.16), color: pageTxt, border: `1.5px solid ${hexA(secondary, 0.7)}` },
      muted: "opacity-70",
      ring: "",
      ringStyle: { boxShadow: `0 0 0 4px ${secondary}` },
      qrColor: secondary,
    };
  }

  // nero elegante — sfondo scuro fisso, accento personalizzabile
  const accent = data.accent_color || "#F59E0B";
  return {
    pageStyle: {},
    pageClass: "bg-gradient-to-b from-zinc-950 via-zinc-900 to-black text-zinc-100",
    cardClass: "bg-zinc-900/80 border border-zinc-800/80 backdrop-blur-xl",
    saveBtn: "font-bold hover:brightness-105",
    saveBtnStyle: { backgroundColor: accent, color: contrastText(accent), boxShadow: `0 10px 30px -10px ${hexA(accent, 0.5)}` },
    secondaryBtn: "hover:brightness-110 font-semibold",
    secondaryBtnStyle: { backgroundColor: hexA(accent, 0.14), color: "#fafafa", border: `1.5px solid ${hexA(accent, 0.55)}` },
    muted: "text-zinc-400",
    ring: "",
    ringStyle: { boxShadow: `0 0 0 4px ${hexA(accent, 0.55)}` },
    qrColor: accent,
  };
}

function digits(s) {
  return (s || "").replace(/[^0-9+]/g, "").replace(/^\+/, "");
}
function ensureHttp(u) {
  if (!u) return "";
  return u.startsWith("http") ? u : `https://${u}`;
}
function socialUrl(net, val) {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  const bases = {
    instagram: "https://instagram.com/",
    facebook: "https://facebook.com/",
    linkedin: "https://linkedin.com/in/",
    tiktok: "https://tiktok.com/@",
  };
  return bases[net] + val.replace(/^@/, "");
}

function ActionBtn({ testId, href, className, style, icon: Icon, label, interactive }) {
  const cls = `w-full flex items-center justify-center gap-2.5 rounded-2xl px-4 py-3.5 text-[15px] font-semibold transition-transform duration-200 active:scale-[0.98] hover:scale-[1.02] ${className}`;
  if (interactive && href) {
    return (
      <a data-testid={testId} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className={cls} style={style}>
        <Icon size={19} /> <span>{label}</span>
      </a>
    );
  }
  return (
    <div data-testid={testId} className={cls} style={style}>
      <Icon size={19} /> <span>{label}</span>
    </div>
  );
}

function Logo({ url, size, shape, ringClass, ringStyle }) {
  const px = { small: 68, medium: 96, large: 132 }[size] || 96;
  if (!url) {
    return (
      <div className={`mb-5 flex items-center justify-center rounded-3xl bg-white/95 ring-4 ${ringClass}`}
        style={{ width: px, height: px, ...ringStyle }}>
        <Building2 className="text-slate-400" size={Math.round(px * 0.36)} />
      </div>
    );
  }
  if (shape === "square") {
    return (
      <div className={`relative mb-5 overflow-hidden rounded-3xl bg-white/95 ring-4 ${ringClass}`}
        style={{ width: px, height: px, ...ringStyle }}>
        <img src={url} alt="logo" className="h-full w-full object-cover" />
      </div>
    );
  }
  if (shape === "rectangle") {
    return (
      <div className={`mb-5 flex items-center justify-center overflow-hidden rounded-2xl bg-white/95 ring-4 ${ringClass} px-4`}
        style={{ height: px, width: Math.min(px * 3, 320), maxWidth: "100%", ...ringStyle }}>
        <img src={url} alt="logo" className="max-h-full max-w-full object-contain" style={{ maxHeight: px - 16 }} />
      </div>
    );
  }
  // original: mantiene le proporzioni, altezza fissa, larghezza automatica
  return (
    <div className={`mb-5 inline-flex items-center justify-center rounded-2xl bg-white/95 ring-4 ${ringClass} p-2`}
      style={{ maxWidth: "100%", ...ringStyle }}>
      <img src={url} alt="logo" className="object-contain" style={{ height: px, width: "auto", maxWidth: "100%" }} />
    </div>
  );
}

// data: card fields, logoUrl: absolute url, qrUrl: absolute png url, vcardUrl, interactive
export default function CardContent({ data, logoUrl, qrUrl, vcardUrl, interactive = false }) {
  const t = getTheme(data);
  const name = data.full_name || "";
  const company = data.company_name || "La tua azienda";
  const socials = [
    { net: "instagram", icon: Instagram, val: data.instagram },
    { net: "facebook", icon: Facebook, val: data.facebook },
    { net: "linkedin", icon: Linkedin, val: data.linkedin },
    { net: "tiktok", icon: Music2, val: data.tiktok },
  ].filter((s) => s.val);

  return (
    <div className={`min-h-full w-full ${t.pageClass}`} style={t.pageStyle} data-testid="public-card-container">
      <div className="mx-auto w-full max-w-md px-5 pt-10 pb-8 flex flex-col items-center">
        {/* Logo */}
        <Logo url={logoUrl} size={data.logo_size} shape={data.logo_shape} ringClass={t.ring} ringStyle={t.ringStyle} />

        <h1 className="font-display text-2xl font-extrabold tracking-tight text-center">{company}</h1>
        {name && <p className="mt-0.5 text-base font-medium opacity-90 text-center">{name}</p>}
        {data.profession && <p className={`mt-1 text-sm ${t.muted} text-center`}>{data.profession}</p>}
        {data.description && (
          <p className="mt-4 text-sm leading-relaxed text-center opacity-80 max-w-xs">{data.description}</p>
        )}

        {/* Actions */}
        <div className="mt-7 w-full space-y-3">
          <ActionBtn testId="btn-save-vcard" interactive={interactive} href={vcardUrl} className={t.saveBtn} style={t.saveBtnStyle} icon={UserPlus} label="Salva contatto" />
          {data.phone && (
            <ActionBtn testId="btn-call-now" interactive={interactive} href={`tel:${data.phone}`} className={t.secondaryBtn} style={t.secondaryBtnStyle} icon={Phone} label="Chiama" />
          )}
          {data.email && (
            <ActionBtn testId="btn-email-now" interactive={interactive} href={`mailto:${data.email}`} className={t.secondaryBtn} style={t.secondaryBtnStyle} icon={Mail} label="Email" />
          )}
          {data.website && (
            <ActionBtn testId="btn-visit-website" interactive={interactive} href={ensureHttp(data.website)} className={t.secondaryBtn} style={t.secondaryBtnStyle} icon={Globe} label="Sito web" />
          )}
          {data.address && (
            <ActionBtn testId="btn-open-maps" interactive={interactive} href={`https://maps.google.com/?q=${encodeURIComponent(data.address)}`} className={t.secondaryBtn} style={t.secondaryBtnStyle} icon={MapPin} label={data.address} />
          )}
          {data.whatsapp && (
            <ActionBtn testId="btn-whatsapp-now" interactive={interactive} href={`https://wa.me/${digits(data.whatsapp)}`} className={t.secondaryBtn} style={t.secondaryBtnStyle} icon={MessageCircle} label="WhatsApp" />
          )}
        </div>

        {/* Socials */}
        {socials.length > 0 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            {socials.map((s) => {
              const Icon = s.icon;
              const href = socialUrl(s.net, s.val);
              const inner = <Icon size={20} />;
              const cls = `flex h-12 w-12 items-center justify-center rounded-2xl transition-transform hover:scale-110 ${t.secondaryBtn}`;
              return interactive ? (
                <a key={s.net} data-testid={`social-link-${s.net}`} href={href} target="_blank" rel="noopener noreferrer" className={cls} style={t.secondaryBtnStyle}>{inner}</a>
              ) : (
                <div key={s.net} data-testid={`social-link-${s.net}`} className={cls} style={t.secondaryBtnStyle}>{inner}</div>
              );
            })}
          </div>
        )}

        {/* QR */}
        {qrUrl && (
          <div className={`mt-7 rounded-3xl p-4 ${t.cardClass}`} data-testid="public-card-qr">
            <div className="flex items-center gap-2 mb-2 justify-center opacity-80" style={{ color: t.qrColor }}>
              <QrCode size={16} /> <span className="text-xs font-semibold uppercase tracking-wider">Scansiona</span>
            </div>
            <img src={qrUrl} alt="QR code" className="h-40 w-40 rounded-xl bg-white p-2" />
          </div>
        )}

        <p className="mt-8 text-[11px] uppercase tracking-widest opacity-40">Creato con TapCard Manager</p>
      </div>
    </div>
  );
}
